import omit from 'lodash/omit.js';

import type { BaseEventListenerProps } from './AbstractEventListener';
import { AbstractEventListener } from './AbstractEventListener';
import type { BasePermission, EmptyObject, FlatContext, LoggedEvent } from './loggingTypes';
import { LoggingEventType } from './loggingTypes';

export type ConsoleEventListenerProps<LoggingContext, CustomEvents> = {
  /** Returns a selection fields to log to the console. */
  getFlatContext?: (context: Partial<LoggingContext>) => FlatContext;

  /**
   * For custom events allows for custom formatting.
   *
   * If this is not specified, custom events are printed using JSON.stringify.
   */
  customEventFormatter?: (
    event: CustomEvents,
    context: Partial<LoggingContext>
  ) => unknown | unknown[] | null;
  /** Transform for the arguments passed to the console. Useful for adding colors. */
  messageTransform?: (type: LoggingEventType, args: unknown[]) => unknown[];
} & BaseEventListenerProps<LoggingContext, CustomEvents>;

/** Rudimentary event listener that logs to the console. */
export class ConsoleEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  OtherEvents extends EmptyObject = EmptyObject,
  Permission extends BasePermission = BasePermission
> extends AbstractEventListener<LoggingContext, OtherEvents, Permission> {
  private readonly props: ConsoleEventListenerProps<LoggingContext, OtherEvents>;
  private readonly printConsole: Console;

  constructor(
    props?: ConsoleEventListenerProps<LoggingContext, OtherEvents>,
    printConsole: Console = console
  ) {
    super({
      ...props,
      requiredPermissions: ['logging'],
    });
    this.props = props ?? {};
    this.printConsole = printConsole;
  }

  protected logEventInternal = (
    event: LoggedEvent<OtherEvents>,
    context: Partial<LoggingContext>
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: This is fine.
  ): void => {
    let contextDict: Record<string, unknown> | undefined;

    if (this.props.getFlatContext) {
      const flatContext = this.props.getFlatContext(context) ?? {};

      for (const [key, value] of Object.entries(flatContext)) {
        if (!value) continue;
        if (!contextDict) contextDict = {};
        contextDict[String(key)] = value;
      }
    }

    switch (event.type) {
      case LoggingEventType.DEBUG: {
        const messageAndArgs = [`${event.component}: ${event.message}`, contextDict ?? ''];
        this.printConsole.debug(...this.transform(event.type, messageAndArgs));
        break;
      }

      case LoggingEventType.INFO: {
        const maybeLabel = event.label ? ` (${event.label})` : '';
        const messageAndArgs = [
          `${event.component}: ${event.action}${maybeLabel}`,
          contextDict ?? '',
        ];
        this.printConsole.info(...this.transform(event.type, messageAndArgs));
        break;
      }

      case LoggingEventType.USER_ACTION: {
        const maybeLabel = event.label ? ` (${event.label})` : '';
        const messageAndArgs = [
          `${event.component}: ${event.action}${maybeLabel}`,
          contextDict ?? '',
        ];
        this.printConsole.info(...this.transform(event.type, messageAndArgs));
        break;
      }

      case LoggingEventType.VALUE: {
        const maybeLabel = event.label ? ` (${event.label})` : '';
        const messageAndArgs = [
          `${event.component}: ${event.variable} = ${event.value.toLocaleString()}${maybeLabel}`,
          contextDict ?? '',
        ];
        this.printConsole.info(...this.transform(event.type, messageAndArgs));
        break;
      }

      case LoggingEventType.TIMING: {
        const maybeLabel = event.label ? ` (${event.label})` : '';
        const messageAndArgs = [
          `${event.component}: ${event.variable} = ${Math.round(event.valueMs)}ms${maybeLabel}`,
          contextDict ?? '',
        ];
        this.printConsole.info(...this.transform(event.type, messageAndArgs));
        break;
      }

      case LoggingEventType.WARNING: {
        const messageAndArgs = [`${event.component}: ${event.message}`, contextDict ?? ''];
        this.printConsole.warn(...this.transform(event.type, messageAndArgs));
        break;
      }

      case LoggingEventType.ERROR: {
        const maybeAction = event.action ? ` (${event.action})` : '';
        const messageAndArgs = [
          `${event.component}: ${event.message}${maybeAction}`,
          event.error,
          contextDict ?? '',
        ];
        this.printConsole.error(...this.transform(event.type, messageAndArgs));
        break;
      }

      case LoggingEventType.CUSTOM: {
        const message = this.props.customEventFormatter
          ? this.props.customEventFormatter(event, context)
          : omit(event, 'type');
        if (!message) break;

        const messageAndArgs = [
          'Custom:',
          ...(Array.isArray(message) ? message : [message]),
          contextDict ?? '',
        ];
        this.printConsole.info(...this.transform(event.type, messageAndArgs));
        break;
      }
    }
  };

  protected transform = (type: LoggingEventType, message: unknown[]): unknown[] =>
    this.props.messageTransform ? this.props.messageTransform(type, message) : message;

  /** @override */
  protected flushInternal: () => Promise<unknown> = () => Promise.resolve();
}
